import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, ChevronRight } from "lucide-react";
import {
  ROAD_PHASES,
  COMPLIANCE_GOAL,
  DPDP_COMPLIANCE_DEADLINE,
  roadPhaseStatus,
  daysUntilDeadline,
  formatDeadlineDate,
  phaseTargetDates,
  roadProgressScore,
  completedRoadPhases,
  formatStartDate,
} from "../../data/complianceRoadmap";
const ROAD = {
  incomplete: "#ef4444",
  incompleteTrack: "#fecaca",
  complete: "#22c55e",
  completeTrack: "#bbf7d0",
};

function formatToday(date = new Date()) {
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTodayShort(date = new Date()) {
  return {
    day: date.getDate(),
    month: date.toLocaleDateString("en-IN", { month: "short" }),
    year: date.getFullYear(),
  };
}

const STATUS_STYLE = {
  complete: {
    node: "bg-emerald-500 text-white shadow-lg shadow-emerald-200/60 border-2 border-emerald-400",
    label: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    badgeText: "Done",
    iconColor: "#ffffff",
    dotColor: ROAD.complete,
  },
  in_progress: {
    node: "bg-red-50 border-2 border-red-400 shadow-md shadow-red-100/80",
    label: "text-red-700",
    badge: "bg-red-100 text-red-700 border-red-200",
    badgeText: "In progress",
    iconColor: ROAD.incomplete,
    dotColor: ROAD.incomplete,
  },
  pending: {
    node: "bg-red-50 text-red-400 border-2 border-red-200 shadow-sm",
    label: "text-red-600",
    badge: "bg-red-50 text-red-600 border-red-200",
    badgeText: "Pending",
    iconColor: ROAD.incomplete,
    dotColor: ROAD.incomplete,
  },
};

const ASSESSMENT_PHASE_IDS = new Set(["assess", "pia", "ropa", "tprm"]);

const ROAD_PHASE_MODULE = {
  assess: "gap",
  pia: "dpia",
  ropa: "ropa",
  tprm: "tprm",
  breach: "breach",
};

function phaseAssessmentMeta(phaseId, deptProgress) {
  if (!ASSESSMENT_PHASE_IDS.has(phaseId)) return null;
  const data = deptProgress?.[phaseId];
  if (!data?.departments_total) return null;

  const deptTotal = data.departments_total ?? 0;
  const deptCompleted = data.departments_complete ?? 0;

  return {
    deptCompleted,
    deptTotal,
  };
}

function AssessmentProgressMeta({ meta }) {
  if (!meta) return null;

  const lineClass =
    "text-[10px] font-bold text-slate-900 tabular-nums leading-snug";
  const labelClass = "font-bold text-slate-700";

  return (
    <div className="mt-1 space-y-0.5 w-full max-w-[120px]">
      <p className={lineClass}>
        <span className={labelClass}>Depts:</span> {meta.deptCompleted}/
        {meta.deptTotal} done
      </p>
    </div>
  );
}

function RoadNode({
  phase,
  progress,
  targetDate,
  index,
  onClick,
  compact,
  assessmentMeta,
}) {
  const status = roadPhaseStatus(phase.id, progress);
  const pct = Math.round(progress?.[phase.id] ?? 0);
  const style = STATUS_STYLE[status];
  const Icon = phase.icon;
  const isActive = status === "in_progress";
  const isComplete = status === "complete";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.08 + index * 0.07,
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -2 }}
      className={`group relative flex flex-col items-center text-center z-10 ${
        compact ? "min-w-[80px] sm:min-w-[88px] w-full" : "w-full max-w-[140px]"
      }`}
    >
      <div className="relative mb-2">
        {isActive && (
          <span
            className="absolute inset-0 rounded-full animate-ping opacity-25"
            style={{ background: ROAD.incomplete }}
          />
        )}
        <div
          className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105 ${style.node}`}
        >
          <Icon
            size={compact ? 18 : 20}
            strokeWidth={2.25}
            style={{ color: style.iconColor }}
          />
        </div>
        {!isComplete && pct > 0 && (
          <span className="absolute -bottom-1 -right-1 min-w-[22px] h-[18px] px-1 rounded-full text-[9px] font-bold tabular-nums flex items-center justify-center bg-white border border-red-200 shadow-sm text-red-600">
            {pct}%
          </span>
        )}
        {isComplete && (
          <span className="absolute -bottom-1 -right-1 min-w-[22px] h-[18px] px-1 rounded-full text-[9px] font-bold tabular-nums flex items-center justify-center bg-emerald-500 text-white border border-emerald-400 shadow-sm">
            ✓
          </span>
        )}
      </div>

      <span
        className={`text-[10px] sm:text-xs font-bold leading-tight whitespace-nowrap ${style.label} transition-colors`}
      >
        {phase.short}
      </span>
      {!compact && (
        <span className="text-[9px] text-slate-500 mt-0.5 leading-tight hidden sm:block px-1">
          by{" "}
          {targetDate.toLocaleDateString("en-IN", {
            month: "short",
            year: "numeric",
          })}
        </span>
      )}
      <span
        className={`mt-1 text-[8px] font-bold px-1.5 py-0.5 rounded-full border ${style.badge}`}
      >
        {style.badgeText}
      </span>
      <AssessmentProgressMeta meta={assessmentMeta} />
    </motion.button>
  );
}

function GoalNode({ roadScore, complianceLabel, loading }) {
  const Icon = COMPLIANCE_GOAL.icon;
  const allDone = roadScore >= 100;
  const ringColor = allDone ? ROAD.complete : ROAD.incomplete;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.55, duration: 0.5 }}
      className="flex flex-col items-center text-center min-w-[88px] w-full z-10 flex-shrink-0"
    >
      <div className="relative mb-2 inline-flex items-center justify-center">
        <div className="relative p-[5px]">
          {!loading && (
            <div
              className="absolute inset-0 rounded-[18px] sm:rounded-[20px] pointer-events-none"
              style={{
                background: `conic-gradient(from 180deg, ${ringColor} ${roadScore * 3.6}deg, transparent ${roadScore * 3.6}deg)`,
                mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))",
                WebkitMask:
                  "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))",
                opacity: 0.6,
              }}
            />
          )}
          <div
            className={`relative z-[1] w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex flex-col items-center justify-center ${
              allDone
                ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xl shadow-emerald-300/40"
                : "bg-gradient-to-br from-red-500 to-red-600 text-white shadow-xl shadow-red-300/40"
            }`}
          >
            <Icon size={18} className="mb-0.5 shrink-0" />
            <span className="text-[10px] font-bold tabular-nums leading-none">
              {loading ? "…" : `${roadScore}%`}
            </span>
          </div>
        </div>
      </div>
      <span
        className={`text-xs sm:text-sm font-bold mt-0 leading-tight ${allDone ? "text-emerald-800" : "text-red-700"}`}
      >
        {COMPLIANCE_GOAL.short}
      </span>
      <span className="text-[9px] text-slate-600 mt-0.5 font-bold">
        {complianceLabel || "Building readiness"}
      </span>
      <div className="mt-1.5 flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200">
        <Calendar size={10} />
        {formatDeadlineDate()}
      </div>
    </motion.div>
  );
}

function StartNode({ startDate }) {
  const start = startDate ? new Date(startDate) : new Date();
  const { day, month } = formatTodayShort(start);

  return (
    <div className="flex flex-col items-center flex-shrink-0 min-w-[92px] w-full z-10 pt-1">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex flex-col items-center justify-center shadow-md shadow-emerald-200/50 border-2 border-emerald-300">
        <span className="text-[8px] font-bold uppercase tracking-wide leading-none">
          Start
        </span>
        <span className="text-base font-bold tabular-nums leading-tight">
          {day}
        </span>
        <span className="text-[9px] font-semibold leading-none">{month}</span>
      </div>
      <span className="text-[10px] text-emerald-900 mt-2 font-extrabold text-center leading-tight whitespace-nowrap">
        {formatToday(start)}
      </span>
      <span className="text-[8px] text-emerald-700 font-bold whitespace-nowrap uppercase tracking-wide">
        Journey began
      </span>
      <span className="mt-1.5 flex items-center gap-1 text-[9px] font-bold text-white bg-slate-800 rounded-full px-2.5 py-1 whitespace-nowrap shadow-sm">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        Today · {formatToday()}
      </span>
    </div>
  );
}
export default function ComplianceRoadmap({
  progress = {},
  loading = false,
  complianceLabel = null,
  deptProgress = null,
  activeUsers = 0,
  startDate = null,
  allowedModules = [],
}) {
  const navigate = useNavigate();
  const daysLeft = daysUntilDeadline();
  const targets = useMemo(
    () =>
      Object.fromEntries(phaseTargetDates().map((t) => [t.id, t.targetDate])),
    [],
  );
  const roadScore = roadProgressScore(progress);
  const completed = completedRoadPhases(progress);
  const total = ROAD_PHASES.length;
  const journeyStart = startDate ? new Date(startDate) : new Date();

  const handlePhaseClick = (phase) => {
    const mod = ROAD_PHASE_MODULE[phase.id];
    if (mod && !allowedModules.includes(mod)) return;
    if (phase.route) navigate(phase.route);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4 animate-pulse">
        <div className="h-8 bg-slate-100 rounded-lg w-2/3" />
        <div className="h-32 bg-slate-100 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-5 py-4 sm:py-5">
      {/* Header strip */}
      <div className="flex items-center justify-between gap-3 mb-6 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-indigo-600 mb-1 whitespace-nowrap">
            <MapPin size={12} className="shrink-0" />
            Road to compliance
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 font-display leading-tight whitespace-nowrap">
            Your DPDP journey
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-lg truncate sm:whitespace-normal">
            Five phased modules leading to full compliance under the Digital
            Personal Data Protection Act, 2023.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 flex-nowrap">
          <div className="rounded-2xl px-4 py-2.5 border border-indigo-100 bg-indigo-50/80 text-center min-w-[88px]">
            <p className="text-[9px] font-bold uppercase tracking-wide text-indigo-600">
              Days left
            </p>
            <p className="text-2xl font-bold tabular-nums text-indigo-700">
              {daysLeft}
            </p>
          </div>
          <div className="rounded-2xl px-4 py-2.5 border border-emerald-100 bg-emerald-50/80 text-center min-w-[88px]">
            <p className="text-[9px] font-bold uppercase tracking-wide text-emerald-600">
              Phases done
            </p>
            <p className="text-2xl font-bold tabular-nums text-emerald-700">
              {completed}/{total}
            </p>
          </div>
        </div>
      </div>

      {/* Desktop: horizontal road */}
      <div className="hidden lg:block relative overflow-x-auto pb-2 -mx-1 px-1">
        <div className="relative min-w-[960px] pt-2 pb-6">
          <svg
            className="absolute inset-x-4 top-[34px] h-[12px] w-[calc(100%-2rem)] pointer-events-none z-0"
            viewBox="0 0 920 12"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient
                id="roadGradRed"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="100%" stopColor="#dc2626" />
              </linearGradient>
            </defs>
            <path
              d="M 0 6 L 920 6"
              fill="none"
              stroke={ROAD.incompleteTrack}
              strokeWidth="12"
              strokeLinecap="round"
            />
            <path
              d="M 0 6 L 920 6"
              fill="none"
              stroke="url(#roadGradRed)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="8 10"
              opacity="0.9"
            />
            <motion.path
              d="M 0 6 L 920 6"
              fill="none"
              stroke={ROAD.complete}
              strokeWidth="8"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: completed / total }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>

          <div className="relative z-10 grid grid-cols-7 gap-2 items-start">
            <StartNode startDate={startDate} />

            {ROAD_PHASES.map((phase, i) => (
              <RoadNode
                key={phase.id}
                phase={phase}
                progress={progress}
                targetDate={targets[phase.id]}
                index={i}
                onClick={() => handlePhaseClick(phase)}
                compact
                assessmentMeta={phaseAssessmentMeta(phase.id, deptProgress)}
              />
            ))}

            <GoalNode
              roadScore={roadScore}
              complianceLabel={complianceLabel}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* Mobile / tablet: vertical road */}
      <div className="lg:hidden relative pl-6">
        <div
          className="absolute left-[11px] top-3 bottom-3 w-1.5 rounded-full"
          style={{ background: ROAD.incompleteTrack }}
        />
        <motion.div
          className="absolute left-[11px] top-3 w-1.5 rounded-full origin-top"
          style={{ background: ROAD.complete, height: "calc(100% - 24px)" }}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: completed / total }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        />

        <div className="space-y-5 pb-2">
          <div className="relative flex items-start gap-4 pb-1">
            <div
              className="absolute -left-6 top-5 w-3 h-3 rounded-full border-2 border-white shadow-sm z-10"
              style={{ background: ROAD.complete }}
            />
            <div className="flex-1 rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/80 p-3 shadow-md shadow-emerald-200/50 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex flex-col items-center justify-center shadow-md border-2 border-emerald-300 flex-shrink-0">
                <span className="text-[7px] font-bold uppercase leading-none">
                  Start
                </span>
                <span className="text-sm font-bold tabular-nums leading-tight">
                  {formatTodayShort(journeyStart).day}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-emerald-900">
                  Journey began
                </p>
                <p className="text-xs font-bold text-emerald-700">
                  {formatToday(journeyStart)}
                </p>
                <p className="flex items-center gap-1 mt-1.5 text-[11px] font-bold text-white bg-slate-800 rounded-full px-2.5 py-1 w-fit shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Today · {formatToday()}
                </p>
              </div>
            </div>
          </div>

          {ROAD_PHASES.map((phase) => {
            const status = roadPhaseStatus(phase.id, progress);
            const pct = Math.round(progress?.[phase.id] ?? 0);
            const style = STATUS_STYLE[status];
            const Icon = phase.icon;
            const targetDate = targets[phase.id];
            const isComplete = status === "complete";
            const assessmentMeta = phaseAssessmentMeta(phase.id, deptProgress);

            return (
              <button
                key={phase.id}
                type="button"
                onClick={() => handlePhaseClick(phase)}
                className="relative flex items-start gap-4 w-full text-left group"
              >
                <div
                  className="absolute -left-6 top-5 w-3 h-3 rounded-full border-2 border-white shadow-sm z-10"
                  style={{ background: style.dotColor }}
                />
                <div
                  className={`flex-1 min-w-0 rounded-2xl border p-3 shadow-sm group-hover:shadow-md transition-shadow ${
                    isComplete
                      ? "border-emerald-200 bg-emerald-50/80"
                      : "border-red-200 bg-red-50/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${style.node}`}
                    >
                      <Icon
                        size={18}
                        strokeWidth={2.25}
                        style={{ color: style.iconColor }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-sm font-bold ${isComplete ? "text-emerald-900" : "text-red-900"}`}
                        >
                          {phase.label}
                        </p>
                        <span
                          className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full border ${style.badge}`}
                        >
                          {isComplete ? `${pct}% done` : style.badgeText}
                        </span>
                      </div>
                      <p className="text-[10px] font-bold text-slate-700 mt-0.5">
                        Target:{" "}
                        {targetDate.toLocaleDateString("en-IN", {
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                      {assessmentMeta && (
                        <div className="mt-1 space-y-0.5">
                          <p className="text-[10px] font-bold text-slate-900 tabular-nums">
                            <span className="font-bold text-slate-700">
                              Departments:
                            </span>{" "}
                            {assessmentMeta.deptCompleted}/{assessmentMeta.deptTotal}{" "}
                            done
                          </p>
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      size={16}
                      className={`flex-shrink-0 ${isComplete ? "text-emerald-400 group-hover:text-emerald-600" : "text-red-300 group-hover:text-red-500"}`}
                    />
                  </div>
                  <p className="text-[10px] text-slate-600 mt-2 leading-relaxed">
                    {phase.description}
                  </p>
                </div>
              </button>
            );
          })}

          <div className="relative flex items-start gap-4 pt-2">
            <div
              className="absolute -left-6 top-6 w-3 h-3 rounded-full border-2 border-white shadow-sm z-10"
              style={{
                background: roadScore >= 100 ? ROAD.complete : ROAD.incomplete,
              }}
            />
            <div
              className={`flex-1 rounded-2xl border p-4 flex items-center justify-between gap-4 ${
                roadScore >= 100
                  ? "border-emerald-200 bg-gradient-to-br from-emerald-50/90 to-teal-50/80"
                  : "border-red-200 bg-gradient-to-br from-red-50/90 to-orange-50/60"
              }`}
            >
              <GoalNode
                roadScore={roadScore}
                complianceLabel={complianceLabel}
                loading={loading}
              />
              <div className="text-left min-w-0">
                <p className="text-sm font-bold text-slate-900">
                  {COMPLIANCE_GOAL.label}
                </p>
                <p className="text-[10px] text-slate-600 mt-1 leading-relaxed">
                  {COMPLIANCE_GOAL.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deadline banner */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-5 rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-amber-100"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,251,235,0.95), rgba(254,243,199,0.6))",
        }}
      >
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-900">
            <span className="font-bold">DPDP compliance timeline:</span>{" "}
            organisations should achieve readiness by{" "}
            <span className="font-bold">{formatDeadlineDate()}</span>
            {daysLeft <= 365 && (
              <span className="text-amber-700">
                {" "}
                — {daysLeft} days remaining
              </span>
            )}
          </p>
        </div>
        <p className="text-[10px] font-bold text-amber-900/80 tabular-nums">
          Target: {DPDP_COMPLIANCE_DEADLINE.getFullYear()} readiness
        </p>
      </motion.div>
    </div>
  );
}
